import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useRole } from "../lib/roleContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { StatusBadge } from "../components/StatusBadge";
import { Sparkles, Send, AlertOctagon, Users, Building2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const EXAMPLES = [
  "The rear stair handrail near the clubhouse is loose. A parent nearly slipped after junior karting.",
  "Paddock A gate was unsupervised at 09:15 — juniors and parents walked through before a marshal arrived.",
  "Two juniors signed up for Saturday have no emergency contact on file.",
  "Marshals' radio at Post 3 cut out during yesterday's test session — needs scrutineering.",
];

export default function Intake() {
  const nav = useNavigate();
  const { role } = useRole();
  const [text, setText] = useState("");
  const [location, setLocation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      const inc = await api.createIncident({ text, location, raised_by: role.name });
      setResult(inc);
      toast.success("Report classified by AI assistant", { description: "Advisory only — awaiting human review." });
    } catch (e) {
      toast.error("Could not submit report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-8" data-testid="intake-page">
      <section className="lg:col-span-3 space-y-6">
        <div>
          <div className="tracking-eyebrow text-muted-foreground">Report something</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold mt-2">Tell us what doesn't feel right.</h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-xl leading-relaxed">
            Plain English is fine. ClubCierge will structure it for the right officer —
            paddock, premises, safeguarding or scrutineering — and keep a record of how it's handled.
          </p>
        </div>

        <Card className="p-6 border-border space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="report" className="text-sm">What happened?</Label>
            <Textarea
              id="report"
              rows={6}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. The rear stair handrail is loose. Someone nearly slipped after junior training."
              data-testid="intake-text-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="loc" className="text-sm">Where? (optional)</Label>
            <Input
              id="loc"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Rear stairwell, behind clubhouse"
              data-testid="intake-location-input"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline text-left"
                onClick={() => setText(ex)}
                data-testid={`intake-example-${EXAMPLES.indexOf(ex)}`}
              >
                Use: “{ex.slice(0, 56)}…”
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between pt-1">
            <div className="text-xs text-muted-foreground">Reporting as <b>{role.name}</b></div>
            <Button onClick={submit} disabled={submitting || !text.trim()} data-testid="intake-submit">
              <Send className="h-4 w-4 mr-1.5" /> {submitting ? "Classifying…" : "Submit report"}
            </Button>
          </div>
        </Card>
      </section>

      <aside className="lg:col-span-2 space-y-4">
        <Card className="p-5 border-border race-card bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 tracking-eyebrow text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5" /> AI risk assistant
            </div>
            <span className="text-[10px] tracking-eyebrow font-mono-ui px-1.5 py-0.5 rounded border border-border signal-bg signal-text">
              Advisory only
            </span>
          </div>
          {!result ? (
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              When you submit, the assistant will structure your report into a category, severity, affected
              groups and recommended next steps for a named human officer to authorise.
            </p>
          ) : (
            <ClassificationView inc={result} onOpen={() => nav(`/incidents/${result.id}`)} />
          )}
        </Card>

        <Card className="p-5 border-border text-xs text-muted-foreground space-y-1 leading-relaxed">
          <div className="flex items-center gap-2 text-foreground"><AlertOctagon className="h-4 w-4" /> What ClubCierge does not do</div>
          <p>It does not approve safeguarding, safety or liability decisions.</p>
          <p>Each material action must be accepted by a named human role.</p>
        </Card>
      </aside>
    </div>
  );
}

function ClassificationView({ inc, onOpen }) {
  const c = inc.ai_classification || {};
  const sev = c.severity || "low";
  const sevStatus = sev === "high" ? "red" : sev === "medium" ? "amber" : "green";
  return (
    <div className="mt-4 space-y-3" data-testid="ai-classification-panel">
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Field label="Category" value={c.category} icon={Building2} />
        <Field label="Severity" valueNode={<StatusBadge status={sevStatus} label={sev} testId="ai-severity-badge" />} />
        <Field label="Affected" value={(c.affected_groups || []).join(", ") || "—"} icon={Users} />
        <Field label="Escalate to" value={(c.escalation_route || []).join(" · ") || "—"} />
      </div>
      <div>
        <div className="text-[11px] tracking-eyebrow text-muted-foreground">Recommended next actions</div>
        <ul className="mt-1.5 space-y-1.5 text-sm">
          {(c.recommended_actions || []).map((a, i) => (
            <li key={i} className="flex gap-2"><span className="font-mono-ui text-muted-foreground">{(i + 1).toString().padStart(2, "0")}</span><span>{a}</span></li>
          ))}
        </ul>
      </div>
      {c.rationale && (
        <div className="text-xs text-muted-foreground italic">“{c.rationale}”</div>
      )}
      <div className="flex items-center justify-between pt-1">
        <span className="text-[10px] font-mono-ui text-muted-foreground">{c._source || "ai"}</span>
        <Button size="sm" variant="outline" onClick={onOpen} data-testid="open-incident-btn">
          Open governance view <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function Field({ label, value, valueNode, icon: Icon }) {
  return (
    <div>
      <div className="text-[11px] tracking-eyebrow text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}{label}
      </div>
      <div className="mt-1 text-sm font-medium">{valueNode || value || "—"}</div>
    </div>
  );
}
